import React, { useRef, useState } from "react";
import {
  ArrowPathIcon,
  ChevronRightIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  FingerPrintIcon,
  LockClosedIcon,
  ServerIcon,
  BuildingStorefrontIcon,
  ChartBarSquareIcon,
  UserGroupIcon,
  MagnifyingGlassCircleIcon,
} from "@heroicons/react/20/solid";
import {
  VideoCameraIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import AnimatedCounter from "./AnimatedCounter";
import PlatformlessAIAnimation from "./PlatformlessAIAnimation";

const primaryFeatures = [
  {
    name: "Create",
    description:
      "Upload your video, audio, art or data content as a secure encrypted digital master or as public domain. Tell your audience through in-built social media.",
    href: "#",
    icon: VideoCameraIcon,
  },
  {
    name: "Share",
    description:
      "Self-publish to stream direct to consumer or share with a distributor to receive instant royalties without intermediaries, automatically paid by smart contracts.",
    href: "#",
    icon: UsersIcon,
  },
  {
    name: "Earn",
    description:
      "Choose revenue streams that aligns with your business; subscription, pay-per-view, NFT markets, ticketing, auction or buy/sell, tipping, merch...",
    href: "#",
    icon: ShoppingCartIcon,
  },
];
const secondaryFeatures = [
  {
    name: "Customise.",
    description:
      "Customise storefront, upload catalogue, set pricing, invite audience.",
    icon: BuildingStorefrontIcon,
  },
  {
    name: "Discover.",
    description:
      "No matter how large or small or how popular or niche, our AI search engine will find your audience.",
    icon: MagnifyingGlassCircleIcon,
  },
  {
    name: "Social media.",
    description:
      "Use in-built social media tools to grow your community and retain your customers.",
    icon: UserGroupIcon,
  },
  {
    name: "Analytics.",
    description:
      "On-chain verification of views, sales and engagement. Plan your business.",
    icon: ChartBarSquareIcon,
  },
  {
    name: "Fundraise.",
    description:
      "Crowdfund projects with tokensiation; turn films into perpetual, revenue‑sharing assets.",
    icon: ArrowPathIcon,
  },
  {
    name: "Network.",
    description:
      "Fabstir is a unified network of apps, businesses and people. Strengthens value for all.",
    icon: ServerIcon,
  },
];
const stats = [
  { id: 1, name: "Creators in our community", value: "8,000+" },
  { id: 2, name: "Community events", value: "100+" },
  { id: 3, name: "In film industry", value: "20yr+" },
  { id: 4, name: "In development", value: "4yr+" },
];
const footerNavigation = {
  solutions: [
    { name: "Hosting", href: "#" },
    { name: "Data Services", href: "#" },
    { name: "Uptime Monitoring", href: "#" },
    { name: "Enterprise Services", href: "#" },
  ],
  support: [
    { name: "Pricing", href: "#" },
    { name: "Documentation", href: "#" },
    { name: "Guides", href: "#" },
    { name: "API Reference", href: "#" },
  ],
  company: [
    { name: "About", href: "#" },
    { name: "Blog", href: "#" },
    { name: "Jobs", href: "#" },
    { name: "Press", href: "#" },
    { name: "Partners", href: "#" },
  ],
  legal: [
    { name: "Claim", href: "#" },
    { name: "Privacy", href: "#" },
    { name: "Terms", href: "#" },
  ],
  social: [
    {
      name: "Facebook",
      href: "https://www.facebook.com/profile.php?id=100094126372123",
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      name: "Instagram",
      href: "https://www.instagram.com/fabstirmedia/p/CxtseaZI0dZ/",
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      name: "X",
      href: "https://twitter.com/FabstirOfficial",
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
        </svg>
      ),
    },
    {
      name: "GitHub",
      href: "https://github.com/Fabstir",
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      name: "YouTube",
      href: "https://www.youtube.com/watch?v=e7_ZDFaKh6c",
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  ],
};

export default function Example() {
  const ctaSectionRef = useRef(null);
  const ecosystemSectionRef = useRef(null);

  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState("");

  const handleGetStartedClick = (e) => {
    e.preventDefault();
    ctaSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setError("");
    setSubscribed(false);

    const formData = new FormData();
    formData.append("email", email);

    try {
      const response = await fetch("send_email.php", {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (result.success) {
        setSubscribed(true);
        setEmail("");
      } else {
        setError("Subscription failed. Please try again.");
      }
    } catch {
      setError("Subscription failed. Please try again.");
    }
  };

  return (
    <div className="bg-brand-dark">
      <main>
        {/* Hero section */}
        <div className="relative isolate overflow-hidden">
          <svg
            aria-hidden="true"
            className="absolute inset-0 -z-10 size-full stroke-white/10 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
          >
            <defs>
              <pattern
                x="50%"
                y={-1}
                id="983e3e4c-de6d-4c3f-8d64-b9761d1534cc"
                width={200}
                height={200}
                patternUnits="userSpaceOnUse"
              >
                <path d="M.5 200V.5H200" fill="none" />
              </pattern>
            </defs>
            <svg x="50%" y={-1} className="overflow-visible fill-gray-800/20">
              <path
                d="M-200 200h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
                strokeWidth={0}
              />
            </svg>
            <rect
              fill="url(#983e3e4c-de6d-4c3f-8d64-b9761d1534cc)"
              width="100%"
              height="100%"
              strokeWidth={0}
            />
          </svg>
          <div
            aria-hidden="true"
            className="absolute left-[calc(50%-4rem)] top-10 -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] lg:left-48 lg:top-[calc(50%-30rem)] xl:left-[calc(50%-24rem)]"
          >
            <div
              style={{
                clipPath:
                  "polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)",
              }}
              className="aspect-[1108/632] w-[69.25rem] bg-gradient-to-r from-brand-pink to-brand-lightpurple opacity-30"
            />
          </div>
          <div className="mx-auto max-w-7xl px-6 pb-24 pt-10 sm:pb-32 lg:flex lg:px-8 lg:py-40">
            <div className="mx-auto max-w-2xl shrink-0 lg:mx-0 lg:pt-8">
              <img
                alt="Fabstir logo"
                src="images/Fabstir_logo_with_name.png"
                className="h-16"
              />
              <div className="mt-24 sm:mt-32 lg:mt-16">
                <span className="inline-flex space-x-6">
                  <span
                    className="rounded-full bg-brand-pink/20 px-3 py-1 text-sm/6 font-semibold text-brand-pink ring-1 ring-inset ring-brand-pink/30 cursor-pointer"
                    onClick={() =>
                      ecosystemSectionRef.current?.scrollIntoView({
                        behavior: "smooth",
                      })
                    }
                  >
                    Our white-label apps
                  </span>
                  <a
                    href="https://www.nonmultiplexcinema.com/?p=2121"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-sm/6 font-medium text-brand-lightpurple hover:underline"
                  >
                    <span>View UI presentation</span>
                    <ChevronRightIcon
                      aria-hidden="true"
                      className="size-5 text-brand-lightpurple"
                    />
                  </a>
                </span>
              </div>
              <h1 className="mt-10 text-pretty text-5xl font-semibold tracking-tight text-brand-pink sm:text-7xl">
                Own your platform. Stream directly to your fans
              </h1>
              <p className="mt-8 text-pretty text-lg font-medium text-brand-lightpurple sm:text-xl/8">
                You decide how to monetise your content; subscription, buy/sell,
                auction, tokenisation, ticketing, merch with full transparent
                analytics
              </p>
              <div className="mt-10 flex items-center gap-x-6">
                <a
                  href="#cta-section"
                  onClick={handleGetStartedClick}
                  className="rounded-md bg-brand-pink px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-purple focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-pink"
                >
                  Get started
                </a>
                <a
                  href="https://www.fabstir.com/files/Fabstir_whitepaper_20221012.pdf"
                  className="text-sm/6 font-semibold text-brand-lightpurple"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read whitepaper <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
            <div className="mx-auto mt-16 flex max-w-2xl sm:mt-24 lg:ml-10 lg:mr-0 lg:mt-0 lg:max-w-none lg:flex-none xl:ml-32">
              <div className="w-full max-w-xs sm:max-w-md md:max-w-lg lg:max-w-3xl mx-auto">
                <img
                  alt="App screenshot"
                  src="images/Steam%20X%20-%20The%20Whipser%20of%20Marsh%20Hollow%20-%20chat%202%20-%20part.jpg"
                  width={624}
                  height={938}
                  className="w-full h-auto rounded-md bg-white/5 shadow-2xl ring-1 ring-brand-pink/20 opacity-90"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Logo cloud */}
        <div
          className="mx-auto mt-8 max-w-7xl px-6 sm:mt-16 lg:px-8"
          ref={ecosystemSectionRef}
        >
          <h2 className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-brand-pink sm:text-5xl lg:text-balance text-center">
            The world’s most innovative ecosystem
          </h2>
          <div className="relative overflow-hidden pt-16">
            <div className="max-w-7xl px-2 lg:px-6">
              <img
                alt="Fabstir Network of decentralised apps"
                src="https://fabstir.com/img2/Fabstir%20Core%20Principles%20diag%20202504.png"
                width={1239}
                height={429}
                className="rounded-xl shadow-2xl ring-1 ring-white/10 mx-auto p-2 sm:p-4 md:p-8"
              />
              <div aria-hidden="true" className="relative">
                <div className="absolute -inset-x-20 bottom-0 bg-gradient-to-t from-gray-900 pt-[3%]" />
              </div>
            </div>
          </div>
          <div className="mx-auto mt-10 grid max-w-lg grid-cols-4 items-center gap-x-8 gap-y-10 sm:max-w-xl sm:grid-cols-6 sm:gap-x-10 lg:mx-0 lg:max-w-none lg:grid-cols-5">
            <img
              alt="Sia"
              src="images/sia_logo%20gray.png"
              width={158}
              height={48}
              className="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
            />
            <img
              alt="S5"
              src="images/S5_gray.png"
              width={158}
              height={48}
              className="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
            />
            <img
              alt="Non-Multiplex Cinema"
              src="images/LogoNonMX_gray.png"
              width={158}
              height={48}
              className="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
            />
            {/* <img
              alt="SavvyCal"
              src="https://tailwindcss.com/plus-assets/img/logos/158x48/savvycal-logo-white.svg"
              width={158}
              height={48}
              className="col-span-2 max-h-12 w-full object-contain sm:col-start-2 lg:col-span-1"
            />
            <img
              alt="Statamic"
              src="https://tailwindcss.com/plus-assets/img/logos/158x48/statamic-logo-white.svg"
              width={158}
              height={48}
              className="col-span-2 col-start-2 max-h-12 w-full object-contain sm:col-start-auto lg:col-span-1"
            /> */}
          </div>
        </div>

        {/* Feature section */}
        <div className="mx-auto mt-24 max-w-7xl px-6 sm:mt-44 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base/7 font-semibold text-brand-lightpurple">
              Take control
            </h2>
            <p className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-brand-pink sm:text-5xl lg:text-balance">
              Everything you need to grow your onchain business
            </p>
            <p className="mt-6 text-lg/8 text-brand-gray">
              We empower creators and independent distributors by solving
              industry pain points with user-friendly services and frictionless
              UX. Learn new features at your own pace.
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {primaryFeatures.map((feature) => (
                <div key={feature.name} className="flex flex-col">
                  <dt className="text-base/7 font-semibold text-white">
                    <div className="mb-6 flex size-10 items-center justify-center rounded-lg bg-brand-purple">
                      <feature.icon
                        aria-hidden="true"
                        className="size-6 text-white"
                      />
                    </div>
                    {feature.name}
                  </dt>
                  <dd className="mt-1 flex flex-auto flex-col text-base/7 text-gray-300">
                    <p className="flex-auto">{feature.description}</p>
                    {/* <p className="mt-6">
                      <a
                        href={feature.href}
                        className="text-sm/6 font-semibold text-brand-lightpurple"
                      >
                        Learn more <span aria-hidden="true">→</span>
                      </a>
                    </p> */}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        {/* Feature section */}
        <div className="mt-24 sm:mt-44">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl sm:text-center">
              <h2 className="text-base/7 font-semibold text-brand-lightpurple">
                Everything you need
              </h2>
              <p className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-white sm:text-balance sm:text-5xl">
                Not technical? No problem.
              </p>
              <p className="mt-6 text-lg/8 text-gray-300">
                We handle all backend infrastructure to ensure smooth delivery.
                With end-to-end encryption, you own your customer data. Plan
                your business. Grow your community.
              </p>
            </div>
          </div>
          <div className="relative overflow-hidden pt-16">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <img
                alt="App screenshot"
                src="images/Stream%20X%20-%20homepage%20-%20In%20the%20Shadow%20of%20War.jpg"
                width={1927}
                height={1484}
                className="mb-[-12%] rounded-xl shadow-2xl ring-1 ring-white/10"
              />
              <div aria-hidden="true" className="relative">
                <div className="absolute -inset-x-20 bottom-0 bg-gradient-to-t from-gray-900 pt-[7%]" />
              </div>
            </div>
          </div>
          <div className="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 md:mt-24 lg:px-8">
            <dl className="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-10 text-base/7 text-gray-300 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
              {secondaryFeatures.map((feature) => (
                <div key={feature.name} className="relative pl-9">
                  <dt className="inline font-semibold text-white">
                    <feature.icon
                      aria-hidden="true"
                      className="absolute left-1 top-1 size-5 text-brand-pink"
                    />
                    {feature.name}
                  </dt>{" "}
                  <dd className="inline">{feature.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        {/* Platformless AI section */}
        <div className="mx-auto mt-24 max-w-7xl px-6 sm:mt-44 lg:px-8">
          <div className="mx-auto max-w-2xl sm:text-center">
            <h2 className="text-base/7 font-semibold text-brand-lightpurple">
              Sovereign, secure and private
            </h2>
            <p className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-white sm:text-balance sm:text-5xl">
              Powered by Platformless AI
            </p>
            <p className="mt-6 text-lg/8 text-gray-300">
              Video transcoding, video categorisation, AI search and subtitle
              generation — all running on decentralised GPU hosts with
              end-to-end encryption. No centralised servers. No intermediaries.
              Your content and data stays yours.
            </p>
            <p className="mt-4">
              <a
                href="https://www.platformlessai.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm/6 font-semibold text-brand-pink hover:text-brand-lightpurple"
              >
                Learn more about Platformless AI <span aria-hidden="true">&rarr;</span>
              </a>
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-3xl sm:mt-20">
            <PlatformlessAIAnimation />
          </div>
        </div>

        {/* Stats */}
        <div className="mx-auto mt-24 max-w-7xl px-6 sm:mt-44 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl">
            <h2 className="text-base/8 font-semibold text-brand-lightpurple">
              Our track record
            </h2>
            <p className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-white sm:text-5xl">
              Trusted by thousands of creators&nbsp;worldwide
            </p>
            <p className="mt-6 text-lg/8 text-gray-300">
              Jules Lai (founder) gives presentation at Bringing Entertainment
              Onchain event by Fabstir with Base team from Coinbase, 23rd Jan
              2025, London
            </p>

            <div className="flex justify-center ">
              {/* <div aria-hidden="true" className="relative">
              <div className="absolute -inset-x-20 bottom-0 bg-gradient-to-t from-gray-900 pt-[7%]" />
            </div> */}

              {/* <p className="mt-8 text-pretty text-lg font-medium text-brand-lightpurple sm:text-xl/8">
                You decide how to monetise your content; subscription, buy/sell,
                auction, tokenisation, ticketing. merch and receive full
                transparent analytics
              </p> */}
            </div>
          </div>

          <div className="max-w-2xl flex-none sm:max-w-5xl lg:max-w-xl mt-2">
            <img
              alt="Bringing Entertain Onchain event by Fabstir with Base from Coinbase"
              src="https://fabstir.com/img/events/base202501/IMG-20250124-WA0001v1.jpg"
              width={2432}
              height={1442}
              className="mb-[-12%] rounded-xl shadow-2xl ring-1 ring-white/10"
            />
          </div>

          <dl className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-10 text-white sm:mt-20 sm:grid-cols-2 sm:gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-4">
            {stats.map((stat) => (
              <div
                key={stat.id}
                className="flex flex-col gap-y-3 border-l border-white/10 pl-6"
              >
                <dt className="text-sm/6">{stat.name}</dt>
                <dd className="order-first text-3xl font-semibold tracking-tight">
                  <AnimatedCounter 
                    targetValue={stat.value}
                    duration={2000}
                  />
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* CTA section */}
        <div
          ref={ctaSectionRef}
          id="cta-section"
          className="relative isolate mt-32 px-6 py-32 sm:mt-56 sm:py-40 lg:px-8"
        >
          <svg
            aria-hidden="true"
            className="absolute inset-0 -z-10 size-full stroke-white/10 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
          >
            <defs>
              <pattern
                x="50%"
                y={0}
                id="1d4240dd-898f-445f-932d-e2872fd12de3"
                width={200}
                height={200}
                patternUnits="userSpaceOnUse"
              >
                <path d="M.5 200V.5H200" fill="none" />
              </pattern>
            </defs>
            <svg x="50%" y={0} className="overflow-visible fill-gray-800/20">
              <path
                d="M-200 0h201v201h-201Z M400 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
                strokeWidth={0}
              />
            </svg>
            <rect
              fill="url(#1d4240dd-898f-445f-932d-e2872fd12de3)"
              width="100%"
              height="100%"
              strokeWidth={0}
            />
          </svg>
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-10 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
          >
            <div
              style={{
                clipPath:
                  "polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)",
              }}
              className="aspect-[1108/632] w-[69.25rem] flex-none bg-gradient-to-r from-brand-pink to-brand-lightpurple opacity-30"
            />
          </div>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-balance text-4xl font-semibold tracking-tight text-brand-pink sm:text-5xl">
              Give your business Web3 enabled superpowers.
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg/8 text-brand-lightpurple">
              Get notified when we’re launching white-label apps so you can get
              started right away. Receive the latest news, articles, and
              resources.
            </p>

            <form
              className="mx-auto mt-10 flex max-w-md gap-x-4"
              onSubmit={handleSubscribe}
            >
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                required
                placeholder="Enter your email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="min-w-0 flex-auto rounded-md bg-white/5 px-3.5 py-2 text-base text-white outline outline-1 -outline-offset-1 outline-white/10 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-white sm:text-sm/6"
              />
              <button
                type="submit"
                className="flex-none rounded-md bg-brand-pink hover:bg-brand-purple px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {error ? "Error!" : subscribed ? "Subscribed!" : "Subscribe"}
              </button>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer
        className="mx-auto max-w-7xl px-6 lg:px-8"
        style={{ zIndex: 50, position: "relative" }}
      >
        <div className="border-t border-brand-gray py-12 md:flex md:items-center md:justify-between">
          <div className="flex justify-center gap-x-6 md:order-2">
            {footerNavigation.social.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-brand-lightpurple hover:text-brand-pink"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="sr-only">{item.name}</span>
                <item.icon aria-hidden="true" className="size-6" />
              </a>
            ))}
          </div>
          <p className="mt-8 text-center text-sm/6 text-brand-gray md:order-1 md:mt-0">
            &copy; 2025 Fabstir. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
